import { ReactNode } from 'react';
import { ClockSkewNotice } from './ClockSkewNotice';
import { ArrowLeft } from '@/components/icons';
import { FeatureTicker } from '@/components/auth/FeatureTicker';

/**
 * The signed-out page frame: hero photo on the left, white panel on the right.
 *
 * Extracted from Auth.tsx rather than copied into the password pages. Those had
 * drifted onto the previous design — a centred glass card on floating blurred
 * orbs with a gradient-clipped title — so a customer who clicked "Forgot
 * password" left the lander-styled sign-in and landed on something from an
 * older version of the product. At exactly the moment they are least sure they
 * are in the right place, and most alert to anything that looks like a phishing
 * page.
 *
 * One shell means the next restyle cannot leave the reset flow behind again.
 */
export function AuthShell({
  title,
  titleAccent = '.',
  subtitle,
  children,
}: {
  /** Main heading. The accent below is appended in brand red. */
  title: string;
  /** Trailing accent character, red. Pass '' to omit. */
  titleAccent?: string;
  subtitle?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-screen bg-white text-[#0c0c0e]">
      {/* Left — hero photo: installers putting up C9 holiday lights at dusk. */}
      <div className="relative hidden flex-1 overflow-hidden bg-[#0a0a0f] lg:block">
        <img
          src="/brand/auth-hero-workers.jpg"
          alt="Installers putting up warm-white C9 holiday lights on a modern home at dusk"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent p-9 pt-24">
          <FeatureTicker />
        </div>
      </div>

      {/* Right — panel */}
      <div className="flex w-full flex-col bg-white lg:w-[480px] xl:w-[560px]">
        <div className="flex items-center justify-between px-6 pt-7 lg:px-12">
          <img src="/brand/lightmaster-wordmark.png" alt="LightMaster" className="h-7 w-auto" />
          <a
            href="https://lightmaster.io"
            className="inline-flex items-center gap-1.5 text-[13.5px] text-neutral-500 transition-colors hover:text-[#0c0c0e]"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to site
          </a>
        </div>

        <div className="flex flex-1 items-center justify-center px-6 py-10 lg:px-12">
          <div className="w-full max-w-sm">
            <h1 className="text-[2.1rem] font-medium leading-[1.08] tracking-tight text-[#0c0c0e]">
              {title}
              {titleAccent && <span className="text-[#f70000]">{titleAccent}</span>}
            </h1>
            {subtitle && (
              <p className="mt-3 text-[15px] leading-relaxed text-neutral-500">{subtitle}</p>
            )}
            <ClockSkewNotice />
            <div className="mt-8">{children}</div>
          </div>
        </div>

        <div className="px-6 pb-7 lg:px-12">
          <p className="text-center text-xs text-neutral-400">
            © {new Date().getFullYear()} LightMaster
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * The field and button styles the sign-in form uses, so the password pages match.
 *
 * [color-scheme:light] keeps browser autofill from painting fields dark on a
 * dark-mode OS (see .lm-auth-field:-webkit-autofill in index.css). The base
 * Input flips to focus-visible:bg-card on focus, which is near-black under
 * .dark — that painted the field black-on-black while typing, so the focus
 * background is pinned to white.
 */
export const authInputClass =
  'lm-auth-field [color-scheme:light] h-11 rounded-xl bg-white border-neutral-200 text-[#0c0c0e] placeholder:text-neutral-400 ' +
  'focus-visible:border-[#f70000] focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#f70000]/15 focus-visible:ring-offset-0';

export const authPrimaryButtonClass =
  'h-11 w-full gap-2 rounded-full bg-[#0a0a0f] font-medium text-white transition-transform hover:-translate-y-0.5 hover:bg-[#0a0a0f]/90';

export const authLabelClass = 'text-[13.5px] font-medium text-neutral-600';
// dark-mode-guard: fixed-light-file -- branded auth shell forces light color-scheme to stabilize autofill
